/**
 * Hook to generate FinalReviewItem data for all fields organized into sections
 * Uses formatter registry pattern for clean separation of concerns
 */

import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FIELD_LABELS} from './constants'
import {
  useFieldFormatters,
  type FormatterContext,
  type FinalReviewSection,
  type FinalReviewItemData
} from './fieldFormatters'
import {useOrderManagementFieldConfigurations} from './useOrderManagementFieldConfigurations'
import {useRemovedFieldItems} from './useRemovedFieldItems'

/**
 * Simplified hook - orchestrates the field formatters and organizes them into sections
 * Each field formatter lives in its own file under /fieldFormatters/
 *
 * Also checks for fields that may have been removed (had original values but are now disabled).
 */
export const useFinalReviewItems = (): FinalReviewSection[] => {
  const {t, i18n} = useTranslation()
  const fieldConfigurations = useOrderManagementFieldConfigurations()
  const fieldFormatters = useFieldFormatters()

  const context: FormatterContext = useMemo(
    () => ({
      t,
      language: i18n.language
    }),
    [t, i18n.language]
  )

  // Get set of enabled field IDs for quick lookup
  const enabledFieldIds = useMemo(
    () => new Set(fieldConfigurations.map((config) => config.fieldId)),
    [fieldConfigurations]
  )

  // Get removed fields (fields that had values but are now disabled)
  const removedFieldItems = useRemovedFieldItems({enabledFieldIds, context})

  return useMemo(() => {
    const itemsMap = new Map<string, FinalReviewItemData>()

    // Add changeable fields from configuration
    fieldConfigurations.forEach((config) => {
      const formatter = fieldFormatters[config.fieldId]

      if (!formatter) {
        console.warn(`No formatter found for field: ${config.fieldId}`)
        return
      }

      const {values, requiresApproval, hasChanges} = formatter(context)
      const labelConfig = FIELD_LABELS[config.fieldId]
      const label = labelConfig ? t(labelConfig.key, labelConfig.fallback) : t(config.label)

      itemsMap.set(config.fieldId, {
        fieldId: config.fieldId,
        label,
        values,
        // A field the user did not change cannot require approval — it can sit in
        // `requireDispatchConfirmation` purely because its own lead-time deadline passed.
        requiresApproval: requiresApproval && hasChanges,
        hasChanges
      })
    })

    // Add removed fields (fields that had values but are now disabled)
    removedFieldItems.forEach((item, fieldId) => {
      itemsMap.set(fieldId, item)
    })

    // Add non-changeable fields (Address, Order Placer, and Status)
    const staticFieldIds = ['address', 'orderPlacer', 'status']
    staticFieldIds.forEach((fieldId) => {
      const formatter = fieldFormatters[fieldId]
      if (formatter) {
        const {values, requiresApproval, hasChanges} = formatter(context)
        const labelConfig = FIELD_LABELS[fieldId]
        if (labelConfig) {
          itemsMap.set(fieldId, {
            fieldId,
            label: t(labelConfig.key, labelConfig.fallback),
            values,
            requiresApproval,
            hasChanges
          })
        }
      }
    })

    // Now organize items into sections
    const sections: FinalReviewSection[] = []

    // Section 1: Delivery
    const deliveryFields = ['address', 'date', 'time', 'intervals', 'status', 'driverInstructions']
    const deliveryItems = deliveryFields
      .map((fieldId) => itemsMap.get(fieldId))
      .filter((item): item is FinalReviewItemData => Boolean(item))

    if (deliveryItems.length > 0) {
      sections.push({
        sectionId: 'delivery',
        sectionLabel: t('orderIntake.rmc.orderManagement.sections.delivery', 'Delivery'),
        items: deliveryItems
      })
    }

    // Section 2: Material
    const materialFields = ['material', 'quantity', 'usage', 'materialTesting', 'retarder']
    const materialItems = materialFields
      .map((fieldId) => itemsMap.get(fieldId))
      .filter((item): item is FinalReviewItemData => Boolean(item))

    if (materialItems.length > 0) {
      sections.push({
        sectionId: 'material',
        sectionLabel: t('orderIntake.rmc.orderManagement.sections.material', 'Material'),
        items: materialItems
      })
    }

    // Section 3: Order
    const orderFields = ['orderPlacer', 'siteContact', 'poNumber', 'additionalNotes']
    const orderItems = orderFields
      .map((fieldId) => itemsMap.get(fieldId))
      .filter((item): item is FinalReviewItemData => Boolean(item))

    if (orderItems.length > 0) {
      sections.push({
        sectionId: 'order',
        sectionLabel: t('orderIntake.rmc.orderManagement.sections.order', 'Order'),
        items: orderItems
      })
    }

    return sections
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldConfigurations, removedFieldItems, context, t])
}
